import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { PodsumowanieStanow, RozbicieStanu, StatusStanu, WierszStanu } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import { AtrapaLeku, Karta, Ladowanie, Pole, Pusto, Zakladki } from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { STATUS_STANU } from '../lib/slowniki';
import { data as fData, ilosc, zlote } from '../lib/format';

/**
 * Odpowiednik arkusza „Stany”. Kolumny i reguły statusów przeniesione 1:1;
 * doszła kolejność — pozycje wymagające uwagi idą na górę, bo po to się na ten
 * ekran wchodzi.
 */
export function Stany() {
  const { maUprawnienie } = useAuth();
  const widziCeny = maUprawnienie('ceny.odczyt');
  const [szukaj, setSzukaj] = useState('');
  const [status, setStatus] = useState<StatusStanu | ''>('');
  const [otwarta, setOtwarta] = useState<number | null>(null);

  const parametry = new URLSearchParams({
    ...(szukaj ? { szukaj } : {}),
    ...(status ? { status } : {}),
    naStronie: '200',
  });

  const lista = useQuery({
    queryKey: ['stany', parametry.toString()],
    queryFn: () => api.get<{ pozycje: WierszStanu[] }>(`/stany?${parametry}`),
    // Kilka osób pracuje naraz — stan ma być aktualny bez przeładowywania strony.
    refetchInterval: 15_000,
  });

  const podsumowanie = useQuery({
    queryKey: ['stany', 'podsumowanie'],
    queryFn: () => api.get<PodsumowanieStanow>('/stany/podsumowanie'),
    refetchInterval: 15_000,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Stany magazynowe</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Stan liczony z księgi ruchów: przyjęcia − wydania + korekty. Odświeża się sam co kilkanaście sekund.
          </p>
        </div>
        <PrzyciskExcel dzial="stany" parametry={parametry} />
      </div>

      {podsumowanie.data && (
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-kamien-200 ring-1 ring-kamien-200 sm:grid-cols-4 lg:grid-cols-5">
          <Licznik etykieta="Pozycji w kartotece" wartosc={String(podsumowanie.data.towarow)} />
          <Licznik etykieta="Sztuk na stanie" wartosc={ilosc(podsumowanie.data.sztuk)} />
          {widziCeny && (
            <Licznik etykieta="Wartość magazynu" wartosc={zlote(podsumowanie.data.wartosc)} />
          )}
          <Licznik
            etykieta="Poniżej minimum"
            wartosc={String(podsumowanie.data.ponizejMinimum)}
            ton={podsumowanie.data.ponizejMinimum > 0 ? 'uwaga' : undefined}
          />
          <Licznik
            etykieta="Braki"
            wartosc={String(podsumowanie.data.braki)}
            ton={podsumowanie.data.braki > 0 ? 'blad' : undefined}
          />
        </dl>
      )}

      <Zakladki
        wartosc={status} zmien={setStatus}
        opcje={[
          { wartosc: '', etykieta: 'Wszystkie' },
          { wartosc: 'UZUPELNIJ', etykieta: 'Do uzupełnienia' },
          { wartosc: 'BRAK_STANU', etykieta: 'Braki' },
          { wartosc: 'POWYZEJ_MAX', etykieta: 'Powyżej maksimum' },
          { wartosc: 'OK', etykieta: 'W normie' },
        ]}
      />

      <div className="max-w-md">
        <Pole etykieta="Szukaj" value={szukaj} onChange={(e) => setSzukaj(e.target.value)}
          placeholder="Kod, nazwa albo kod kreskowy" />
      </div>

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Kod</th>
                    <th className="px-4 py-3 font-medium">Nazwa</th>
                    <th className="px-4 py-3 font-medium">Jedn.</th>
                    <th className="px-4 py-3 text-right font-medium">Min</th>
                    <th className="px-4 py-3 text-right font-medium">Max</th>
                    <th className="px-4 py-3 text-right font-medium">Przyjęcia</th>
                    <th className="px-4 py-3 text-right font-medium">Wydania</th>
                    <th className="px-4 py-3 text-right font-medium">Korekty</th>
                    <th className="px-4 py-3 text-right font-medium">Stan</th>
                    <th className="px-4 py-3 text-right font-medium">Rezerw.</th>
                    <th className="px-4 py-3 text-right font-medium">Dostępne</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    {widziCeny && <th className="px-4 py-3 text-right font-medium">Wartość</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((w) => (
                    <Fragment key={w.itemId}>
                      <tr
                        onClick={() => setOtwarta(otwarta === w.itemId ? null : w.itemId)}
                        className="cursor-pointer hover:bg-kamien-50"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-kamien-900">{w.code}</td>
                        <td className="px-4 py-3">
                          <span className="text-kamien-900">{w.name}</span>
                          {w.isDrugDummy && <span className="ml-2"><AtrapaLeku /></span>}
                        </td>
                        <td className="px-4 py-3 text-kamien-600">{w.unit}</td>
                        <td className="px-4 py-3 text-right text-kamien-500">{w.minQty ? ilosc(w.minQty) : '—'}</td>
                        <td className="px-4 py-3 text-right text-kamien-500">{w.maxQty ? ilosc(w.maxQty) : '—'}</td>
                        <td className="px-4 py-3 text-right text-kamien-600">{ilosc(w.przyjecia)}</td>
                        <td className="px-4 py-3 text-right text-kamien-600">{ilosc(w.wydania)}</td>
                        <td className="px-4 py-3 text-right text-kamien-600">{ilosc(w.korekty)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-kamien-900">{ilosc(w.stan)}</td>
                        <td className="px-4 py-3 text-right text-kamien-500">
                          {Number(w.rezerwacje) === 0 ? '—' : ilosc(w.rezerwacje)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-kamien-800">{ilosc(w.dostepne)}</td>
                        <td className={`px-4 py-3 text-xs font-semibold ${STATUS_STANU[w.status].klasa}`}>
                          {STATUS_STANU[w.status].etykieta}
                        </td>
                        {widziCeny && (
                          <td className="px-4 py-3 text-right text-kamien-700">{zlote(w.wartosc)}</td>
                        )}
                      </tr>
                      {otwarta === w.itemId && (
                        <tr>
                          <td colSpan={widziCeny ? 13 : 12} className="bg-kamien-50 px-4 py-4">
                            <Rozbicie itemId={w.itemId} jednostka={w.unit} widziCeny={widziCeny} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Pusto
              tytul={status ? 'Nic w tej kategorii' : 'Brak stanów'}
              opis={status
                ? 'Żadna pozycja nie ma w tej chwili takiego statusu.'
                : 'Stany pojawią się po pierwszym przyjęciu. Wystaw dokument PZ.'}
            />
          )}
        </Karta>
      )}
    </div>
  );
}

function Licznik({ etykieta, wartosc, ton }: {
  etykieta: string; wartosc: string; ton?: 'uwaga' | 'blad';
}) {
  const kolor = ton === 'blad' ? 'text-status-brak' : ton === 'uwaga' ? 'text-status-uzupelnij' : 'text-kamien-900';
  return (
    <div className="bg-white px-4 py-3">
      <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className={`mt-0.5 text-lg font-semibold ${kolor}`}>{wartosc}</dd>
    </div>
  );
}

/** Rozbicie stanu na lokalizacje i partie — w kolejności, w jakiej pójdą do wydania. */
function Rozbicie({ itemId, jednostka, widziCeny }: {
  itemId: number; jednostka: string; widziCeny: boolean;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['stany', itemId],
    queryFn: () => api.get<{ rozbicie: RozbicieStanu[] }>(`/stany/${itemId}`),
  });

  if (isLoading) return <p className="text-sm text-kamien-500">Wczytywanie rozbicia...</p>;
  if (!data || data.rozbicie.length === 0) {
    return <p className="text-sm text-kamien-500">Ta pozycja nie ma obecnie stanu w żadnej lokalizacji.</p>;
  }

  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wide text-kamien-500">
        Rozbicie na lokalizacje i partie — kolejność rozchodu (FEFO)
      </p>
      <table className="w-full max-w-3xl text-sm">
        <tbody className="divide-y divide-kamien-200">
          {data.rozbicie.map((r, i) => (
            <tr key={`${r.location.id}-${r.batch?.id ?? 'bez'}`}>
              <td className="py-1.5 pr-4 text-xs text-kamien-400">{i + 1}.</td>
              <td className="py-1.5 pr-4 text-kamien-700">
                <span className="font-mono text-xs text-kamien-500">{r.location.code}</span> {r.location.name}
              </td>
              <td className="py-1.5 pr-4 font-mono text-xs text-kamien-600">
                {r.batch?.lotNumber ?? 'bez partii'}
              </td>
              <td className="py-1.5 pr-4 text-kamien-600">
                {r.batch?.expiryDate ? `termin ${fData(r.batch.expiryDate)}` : '—'}
              </td>
              <td className="py-1.5 pr-4 text-right font-medium text-kamien-900">
                {ilosc(r.qty)} {jednostka}
              </td>
              {widziCeny && (
                <td className="py-1.5 text-right text-xs text-kamien-500">{zlote(r.batch?.unitCost)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
